import React, { useEffect, useRef } from "react";

export const HOLD_DELAY_MS = 400;
export const REPEAT_MS = 50;

/**
 * Stepper arrow that keeps firing while held, like the native spinner on a
 * `<input type="number">`.
 *
 * Steps once on pointerdown, then after HOLD_DELAY_MS repeats every REPEAT_MS
 * until the pointer is released, leaves the button or the button unmounts.
 * The latest `onStep` is always used, so a callback that closes over the
 * current store value stays correct through a long hold. Keyboard activation
 * (click with `detail === 0`) still steps once. Every other prop is passed
 * through to the `<button>`.
 */
export default function HoldRepeatButton({ onStep, disabled, children, ...rest }) {
  const stepRef = useRef(onStep);
  stepRef.current = onStep;
  const timers = useRef({ delay: null, repeat: null });

  const stop = () => {
    clearTimeout(timers.current.delay);
    clearInterval(timers.current.repeat);
    timers.current.delay = null;
    timers.current.repeat = null;
  };

  useEffect(() => stop, []);

  return (
    <button
      type="button"
      {...rest}
      disabled={disabled}
      onPointerDown={(e) => {
        if (disabled || e.button !== 0) return;
        // Keep focus/selection where it is; the press itself is the step.
        e.preventDefault();
        stop();
        stepRef.current();
        timers.current.delay = setTimeout(() => {
          timers.current.repeat = setInterval(() => stepRef.current(), REPEAT_MS);
        }, HOLD_DELAY_MS);
      }}
      onPointerUp={stop}
      onPointerLeave={stop}
      onPointerCancel={stop}
      onBlur={stop}
      onClick={(e) => {
        if (!disabled && e.detail === 0) stepRef.current();
      }}
    >
      {children}
    </button>
  );
}
